<template>
	<div
		ref="$root"
		class="chat-dialog"
		@mousedown="onMouseDown"
		@click="onOpen"
	>
		<div class="chat-dialog__logo">
			<img
				v-if="props.dialog.PhotoLink"
				v-img-fade="props.dialog.PhotoLink"
				class="chat-dialog__logo-img"
				alt="Avatar"
			/>
			<div
				v-else
				:class="{
					'chat-dialog__logo-text': true,
					'chat-dialog__logo-text--personal': props.dialog.IsPersonal
				}"
			>
				{{ props.dialog.PhotoLiter }}
			</div>
		</div>
		<div class="chat-dialog__body">
			<div class="chat-dialog__body-header">
				<div class="chat-dialog__body-header-title">
					{{ props.dialog.Name }}
				</div>
				<div class="chat-dialog__body-header-datetime">
					<div class="chat-dialog__body-header-datetime-text">
						<span>{{ dialogDate }}</span>
						<span>{{ dialogTime }}</span>
					</div>
					<app-resizable-dropdown
						ref="$deleteDialog"
						name="$deleteDialog"
						position="left"
						:max-width="235"
						centered-on-mobile
						close-by-click-outside
					>
						<template #activator>
							<app-tooltip
								class="chat-dialog__body-header-tooltip"
								:text="$t('remove')"
								position="right"
							>
								<template #activator>
									<app-icon
										class="chat-dialog__body-header-btn"
										name="close-bold"
										size="12px"
										color="#D2D2D2"
										hover-color="#BA4C4C"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>
						<dialog-delete-confirm
							:type="props.dialog.IsPersonal ? 'chat' : 'group'"
							:name="props.dialog.Name"
							@confirm="onDeleteDialog"
							@cancel="$deleteDialog.close()"
						/>
					</app-resizable-dropdown>
				</div>
			</div>
			<div :class="{
				'chat-dialog__body-message': true,
				'chat-dialog__body-message--unreaded': props.dialog.Unreaded && !props.dialog.UnreadCount,
			}"
			>
				<template v-if="props.dialog.ShowSmallPhoto">
					<img
						v-if="props.dialog.SmallPhotoLink"
						v-img-fade="props.dialog.SmallPhotoLink"
						class="chat-dialog__body-message-logo"
						alt="Avatar"
					/>
					<div
						v-else
						:class="{
							'chat-dialog__body-message-text-logo': true,
							'chat-dialog__body-message-text-logo--personal': props.dialog.IsPersonal
						}"
					>
						{{ props.dialog.SmallPhotoLiter }}
					</div>
				</template>
				<div
					class="chat-dialog__body-message-text"
					v-html="messagePreview"
				/>
				<div
					v-if="props.dialog.UnreadCount"
					class="chat-dialog__body-message-unreaded-count"
				>
					{{ props.dialog.UnreadCount }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import DialogDeleteConfirm from "@/components/main/chat-panel/dropdowns/DialogDeleteConfirm.vue";
import moment from "moment/moment";
import { Dialog } from "@/store/chats";
import { formatJoinedUsersMessage, highlightSearchText } from "@/utils/textHelpers";

type ChatDialogProps = {
	dialog: Dialog,
	fromSearch?: string
}

const props = defineProps<ChatDialogProps>()

const emit = defineEmits<{
	(e: 'on-delete', value: number): void,
	(e: 'on-open', value: Dialog): void,
}>()

const $deleteDialog = ref<typeof AppResizableDropdown | null>(null)
const $root = ref<HTMLElement | null>(null)

const dialogMoment = moment(props.dialog.MessageDate);
const dialogDate = dialogMoment.format("DD.MM.YYYY");
const dialogTime = dialogMoment.format("HH:mm");

const messagePreview = computed(() => {
	const message = formatJoinedUsersMessage(props.dialog.Message ?? '');

	if (props.fromSearch) {
		return highlightSearchText(props.fromSearch, message);
	}

	// у обычных многострочных сообщений в превью показываем только первую строку
	return message.split('\n')[0];
});

// порог в пикселях, ниже которого движение мыши считается кликом, а не протяжкой
const DRAG_THRESHOLD = 4
const mouseDownPosition = ref<{ x: number, y: number } | null>(null)
const onMouseDown = (event: MouseEvent) => {
	mouseDownPosition.value = { x: event.clientX, y: event.clientY }
}

// текст выделен именно в этом диалоге (после протяжки или двойного клика по слову)
const hasSelectionInside = () => {
	const selection = window.getSelection()

	if (!selection || selection.isCollapsed || !selection.toString().trim()) {
		return false
	}

	return !!selection.anchorNode && !!$root.value?.contains(selection.anchorNode)
}

const onDeleteDialog = () => emit("on-delete", props.dialog.Id);
const onOpen = (event: MouseEvent) => {
	if ((event?.target as HTMLElement)?.closest('.link-message')) {
		return
	}

	const startPosition = mouseDownPosition.value
	mouseDownPosition.value = null

	const isDrag = !!startPosition && (
		Math.abs(event.clientX - startPosition.x) > DRAG_THRESHOLD
		|| Math.abs(event.clientY - startPosition.y) > DRAG_THRESHOLD
	)

	if (isDrag || hasSelectionInside()) {
		return
	}

	emit("on-open", props.dialog)
};
</script>

<style scoped lang="scss">
.chat-dialog {
	display: flex;
	align-items: center;
	color: #4D4D4D;
	cursor: pointer;
	transition: all 0.1s ease-in;

	&:hover {
		.chat-dialog__body {
			background-color: #F2F2F2;
		}

	}

	&__logo {
		display: flex;
		align-items: center;
		margin-right: 12px;
		height: 40px;
		width: 40px;

		&-img,
		&-text {
			height: 40px;
			width: 40px;
			border-radius: 50%;
		}

		&-text {
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 24px;
			text-transform: uppercase;
			color: #fff;
			background-color: #567CA3;

			&--personal {
				background-color: #E6C15A;
			}
		}
	}

	&__body {
		display: flex;
		flex-direction: column;
		padding: 2px 0;
		border-radius: 8px;
		width: 100%;
		min-height: 54px;

		&-header {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			position: relative;
			width: 100%;
			margin-bottom: 8px;
			padding: 0 4px;

			// @media (max-width: 768px) {
			// 	display: inline;
			// }

			&-title {
				flex: 1 1 auto;
				min-width: 0;
				font-size: 14px;
				line-height: 18px;
				font-weight: 500;
				color: #4D4D4D;
				word-break: break-word;

				// @media (max-width: 768px) {
				// 	position: relative;
				// 	display: inline;
				// }
			}

			&-datetime {
				display: flex;
				flex: 0 1 auto;
				min-width: min-content;
				align-items: flex-start;
				gap: 4px;
				font-size: 12px;
				line-height: 16px;
				color: #8C8C8C;
				margin-top: 1px;

				&-text {
					display: flex;
					flex-wrap: wrap;
					min-width: min-content;
					column-gap: 4px;

					span {
						white-space: nowrap;
					}
				}

				> :last-child {
					flex-shrink: 0;
				}

				// @media (max-width: 768px) {
				// 	display: inline-flex;
				// }
			}

			&-btn {
				color: #999595;

				&:hover {
					color: #499259;
				}
			}
		}

		&-message {
			display: flex;
			align-items: center;
			position: relative;
			height: 100%;
			border-radius: 8px;
			margin-left: 2px;
			width: calc(100% - 4px);

			&--unreaded {
				background-color: #F2F2F2;
			}

			&-logo {
				height: 24px;
				min-width: 24px;
				max-width: 24px;
				border-radius: 50%;
				margin-right: 6px;
			}

			&-text-logo {
				display: flex;
				align-items: center;
				justify-content: center;
				height: 24px;
				min-width: 24px;
				max-width: 24px;
				border-radius: 50%;
				margin-right: 6px;
				color: #fff;
				font-size: 14px;
				line-height: 24px;
				background-color: #567CA3;

				&--personal {
					background-color: #E6C15A;
				}
			}

			&-text {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 1;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: pre-wrap;
				font-size: 14px;
				line-height: 18px;
				border-radius: 5px;
				margin-right: 15px;
				padding-right: 30px;
				padding-left: 2px;
				color: #4D4D4D;
			}

			&-unreaded-count {
				display: flex;
				align-items: center;
				justify-content: center;
				position: absolute;
				right: 12px;
				min-width: 16px;
				height: 16px;
				background-color: #3F884F;
				border-radius: 8px;
				padding: 4px;
				color: #FFFFFF;
				font-size: 12px;
				line-height: 16px;
				font-weight: 500;
				box-shadow: 0 0 4px 0 #33333340;
			}
		}
	}

	&::v-deep(.dropdown-activator) {
		margin-top: -2px;
	}
}
</style>
